import { generated } from '@tempo-io/item-store-dto';
import type { z } from 'zod';

export const FIELD_ASSOCIATION_MODES = {
  ALL: 'ALL',
  CUSTOM: 'CUSTOM',
} as const;

export type CustomField = z.infer<typeof generated.fieldSchema>;

export type CustomFieldTypes = CustomField['type'];

// Read off the schema rather than restated, so a new backend field type cannot go missing here.
export const CUSTOM_FIELD_TYPES: readonly CustomFieldTypes[] =
  generated.fieldSchema.shape.type.options;

const MULTI_VALUE_FIELD_TYPES = new Set<CustomFieldTypes>([
  'MULTI_CUSTOMER',
  'MULTI_ITEM',
  'MULTI_LINK',
  'MULTI_PICKLIST',
  'MULTI_TEXT',
  'MULTI_USER',
]);

export const isMultiValueCustomFieldType = (fieldType: CustomFieldTypes): boolean =>
  MULTI_VALUE_FIELD_TYPES.has(fieldType);

const DIALOG_EDITED_SINGLE_VALUE_FIELD_TYPES = new Set<CustomFieldTypes>([
  'LINK',
  'USER',
  'ITEM',
  'MULTILINE_TEXT',
  'NOTE',
]);

export const isDialogEditedCustomFieldType = (fieldType: CustomFieldTypes): boolean =>
  isMultiValueCustomFieldType(fieldType) || DIALOG_EDITED_SINGLE_VALUE_FIELD_TYPES.has(fieldType);

// Softened relative to the generated schema, which marks both required. They have Kotlin defaults
// (`archived = false`, `itemTypeAssociationMode = ALL`) so the API accepts their absence, but
// dev/scripts/openapi-codegen/infer-required.ts promotes every non-nullable property to required.
// Remove once that script distinguishes "non-nullable" from "no default".
export const createCustomFieldSchema = generated.createFieldRequestSchema.partial({
  archived: true,
  itemTypeAssociationMode: true,
});

export type CreateCustomField = z.infer<typeof createCustomFieldSchema>;

// The endpoint is a PATCH, so this is the patch contract rather than Partial<CreateCustomField>.
export type UpdateCustomField = z.infer<typeof generated.patchFieldRequestSchema>;
